import type { ItemDragAndDropGroup } from '../../types';

const l1ItemKey = Symbol('l1ItemKey');
const nestedItemKey = Symbol('nestedItemKey');
const moreMenuKey = Symbol('moreMenuKey');

// This is the generic data structure shared by all draggable items in the navigation.
export type ItemData = {
	group: ItemDragAndDropGroup;
	isReorderable: boolean;
	label?: string;
	menuItemId: string;
	type: 'menu-item'; // type is used for auto scroll
};

// This is the L1 (level 1 menu item in the sidebar) specific data structure. L1 menu items are different
// from nested items as they can be hidden or shown. There are also a fixed set.
export type TL1ItemData = ItemData & {
	isHidden: boolean;
	isHideable: boolean;
	[l1ItemKey]: true;
};

// This is a nested menu item in the side bar (i.e. any menu item that is not at level 1). It cannot be hidden
// or shown like L1 items, but it has additional properties like entityAri that defines the parent
// entity that the menu item belongs to and the ownerAri that defines the user who owns the menu ordering
// (i.e. the current user).
export type TNestedItemData = ItemData & {
	entityAri: string;
	[nestedItemKey]: true;
	ownerAri: string;
};

export type CreateL1ItemData = (data: Omit<TL1ItemData, typeof l1ItemKey | 'type'>) => TL1ItemData;

export const createL1ItemData: CreateL1ItemData = (data) => ({
	...data,
	type: 'menu-item',
	[l1ItemKey]: true,
});

export const getL1ItemData = (data: TL1ItemData): TL1ItemData => ({
	...data,
});

export const isL1ItemData: IsItemData<TL1ItemData> = (
	data: Record<string | symbol, unknown>,
): data is TL1ItemData => {
	return data[l1ItemKey] === true;
};

export const isL1ItemDataInGroup: IsItemDataInGroup<TL1ItemData> = (
	data: TL1ItemData,
	groups: ItemDragAndDropGroup | ItemDragAndDropGroup[], // 'menu' | ['menu']
) => {
	return Array.isArray(groups) ? groups.includes(data.group) : groups === data.group;
};

export const MORE_MENU_ITEM_ID = Symbol('moreMenuId');
export type MenuItemIdType = string | typeof MORE_MENU_ITEM_ID;

type TMoreMenuData = {
	menuItemId: typeof MORE_MENU_ITEM_ID;
	[moreMenuKey]: true;
};

export function getMoreMenuData(): TMoreMenuData {
	return {
		menuItemId: MORE_MENU_ITEM_ID,
		[moreMenuKey]: true,
	};
}

export function isMoreMenuData(data: Record<string | symbol, unknown>): data is TMoreMenuData {
	return data[moreMenuKey] === true;
}

export type CorrectedNestedItemData = (
	data: Omit<TNestedItemData, typeof nestedItemKey | 'type'>,
) => TNestedItemData;

export const createNestedItemData: CorrectedNestedItemData = (data) => ({
	...data,
	type: 'menu-item',
	[nestedItemKey]: true,
});

export const getNestedItemData = (
	data: ItemData & { entityAri: string; ownerAri: string },
): TNestedItemData => {
	return {
		...data,
		[nestedItemKey]: true,
	};
};

export const isNestedItemData: IsItemData<TNestedItemData> = (
	data: Record<string | symbol, unknown>,
): data is TNestedItemData => {
	return data[nestedItemKey] === true;
};

export const isNestedItemDataInGroup: IsItemDataInGroup<TNestedItemData> = (
	data: TNestedItemData,
	groups: ItemDragAndDropGroup | ItemDragAndDropGroup[], // 'menu' | ['menu']
) => {
	return Array.isArray(groups) ? groups.includes(data.group) : groups === data.group;
};

export type GetItemData<T> = (data: T) => T;

export type IsItemData<T extends Record<string | symbol, unknown>> = (
	data: Record<string | symbol, unknown>,
) => data is T;

export type IsItemDataInGroup<T> = (
	data: T,
	groups: ItemDragAndDropGroup | ItemDragAndDropGroup[],
) => boolean;

export type IsItemDraggable<T> = (data: T) => boolean;

export const OPERATIONS = {
	MOVE_TO_TOP: 'MOVE_TO_TOP' as const,
	MOVE_UP: 'MOVE_UP' as const,
	MOVE_DOWN: 'MOVE_DOWN' as const,
	MOVE_TO_BOTTOM: 'MOVE_TO_BOTTOM' as const,
	BEFORE: 'reorder-before' as const,
	AFTER: 'reorder-after' as const,
};

export type MenuItemReorderOperation =
	| typeof OPERATIONS.MOVE_TO_TOP
	| typeof OPERATIONS.MOVE_UP
	| typeof OPERATIONS.MOVE_DOWN
	| typeof OPERATIONS.MOVE_TO_BOTTOM
	| typeof OPERATIONS.BEFORE
	| typeof OPERATIONS.AFTER;
export type OrderPreference = Array<{ menuId: string; visible: boolean }>;

export type RawItemData = {
	id: string;
	label: string;
};
